// Pure round-trip logic for the booking-wide Lagerplatz (WinterStorage) — the single
// dynamic-price input that is NOT a booking field. Dependency-free (type-only DTO
// import) so it is unit-testable without the Angular-tainted payload/load mappers,
// which both delegate here. There is exactly ONE Lagerplatz per booking (like the
// season), shared by the relation AND the free dynamic services.

import type { BookingV2WinterStorageOption } from '../dtos/booking-v2.dtos';

// A loaded WinterStorage (model) or a dynamicPrice/assignment provenance entry —
// duck-typed so this module needs no model import.
type WinterStorageRef = { id?: number | null; handle?: string | null } | null | undefined;

// Picker option from any winterStorage reference; the label defaults to the id when
// the handle is absent (a deleted/partial reference still renders).
export function toWinterStorageOption(ref: { id: number; handle?: string | null }): BookingV2WinterStorageOption {
    return { id: ref.id, handle: ref.handle ?? String(ref.id) };
}

// The relation-assignment payload patch carrying the chosen Lagerplatz (omitted when
// none) so an open-ended relation round-trips it. Mirrors the membership route.
export function winterStorageAssignmentPatch(winterStorageId: number | null): { winterStorage?: { id: number } } {
    return winterStorageId != null ? { winterStorage: { id: winterStorageId } } : {};
}

// Recover the chosen Lagerplatz on edit. Primary source: the relation assignment
// (where it is persisted — the reliable source for an open-ended relation that emits
// no one-off position). Fallback: the dynamicPrice provenance of any
// position/subscription (a free dynamic service priced by a Lagerplatz, with no
// relation assignment). `null` when no rule branched on a Lagerplatz.
export function recoverWinterStorageOption(
    assignment: WinterStorageRef,
    provenance: ReadonlyArray<WinterStorageRef>,
): BookingV2WinterStorageOption | null {
    const fromAssignment = assignment?.id != null ? assignment : undefined;
    const fromProvenance = provenance.find(w => w?.id != null);
    const ws = fromAssignment ?? fromProvenance;
    return ws?.id != null ? toWinterStorageOption({ id: ws.id, handle: ws.handle }) : null;
}
